import React, { useEffect } from "react";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const CardServices = ({
  title,
  description,
  buttonText,
  cardPath,
  bgImage,
  features,
}) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const borderControls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0.2 });

  useEffect(() => {
    if (inView) {
      borderControls.start({
        width: "100%",
        transition: { duration: 0.5, ease: "easeInOut" },
      });
    } else {
      borderControls.start({
        width: "0%",
        transition: { duration: 0.5, ease: "easeInOut" },
      });
    }
  }, [inView, borderControls]);

  const handleNavigate = (e) => {
    e.preventDefault();
    localStorage.setItem("fromServicesSection", "true"); // Set the flag in localStorage
    navigate(cardPath, { state: { bgImage, description, title, features } });
  };

  return (
    <motion.div
      ref={ref}
      className="group relative bg-black text-white md:w-[300px] w-full p-6 cursor-pointer overflow-hidden rounded-md shadow-lg "
      style={{
        borderLeft: "0.1px solid rgba(255, 255, 255, 0.2)",
        borderRight: "0.1px solid rgba(255, 255, 255, 0.2)",
        borderTop: "0.1px solid rgba(255, 255, 255, 0.2)",
        height: "400px",
      }}
      initial={{ scale: 1 }}
      whileHover={{ scale: 1.05 }}
    >
      <div className="absolute top-0 left-0 h-1 w-full bg-secondary">
        <motion.div
          className="h-full bg-primary"
          initial={{ width: "0%" }}
          animate={borderControls}
        />
      </div>

      <div
        className="flex flex-col justify-between h-full"
        onClick={handleNavigate}
      >
        {/* Title */}
        <motion.h2
          className="text-2xl font-bold mb-4 w-3/4"
          initial={{ y: 48, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ ease: "easeInOut", duration: 0.75 }}
        >
          {title}
        </motion.h2>

        <div className="flex flex-col justify-between flex-grow">
          {/* Description */}
          <motion.p
            className="text-sm text-gray-400 mb-4 overflow-hidden custom-line-clamp-8"
            initial={{ y: 48, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ ease: "easeInOut", duration: 0.75 }}
          >
            {description}
          </motion.p>

          {/* Button */}
          <motion.button
            className="bg-primary text-black py-2 px-4 rounded-lg w-1/2 flex items-center justify-between mt-4 hover:bg-yellow-400 transition-colors duration-300"
            initial={{ y: 48, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ ease: "easeInOut", duration: 0.75 }}
          >
            {buttonText}
            <span className="mx-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="w-5 h-5"
              >
                {isArabic ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M7 8l-4 4m0 0l4 4m-4-4h18"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                  />
                )}
              </svg>
            </span>
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

export default CardServices;
